import { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { Button } from "@/modules/shared/ui/components/button"
import { Input } from "@/modules/shared/ui/components/input"
import { Label } from "@/modules/shared/ui/components/label"
import { useCreateFolder, useRenameFolder } from "../../hooks/useFolders"

interface CreateRenameFolderDialogProps {
  mode: "create" | "rename"
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Required when mode === 'create' to specify the parent folder */
  parentFolderId?: string | null
  /** Required when mode === 'rename' */
  folderId?: string
  existingName?: string
  /** Optional override callback (used by FolderTreeBrowser root create) */
  onCreate?: (name: string) => void
}

export function CreateRenameFolderDialog({
  mode,
  open,
  onOpenChange,
  parentFolderId,
  folderId,
  existingName = "",
  onCreate,
}: CreateRenameFolderDialogProps) {
  const [name, setName] = useState(existingName)
  const createFolder = useCreateFolder()
  const renameFolder = useRenameFolder()

  useEffect(() => {
    if (open) setName(existingName)
  }, [open, existingName])

  const isPending = createFolder.isPending || renameFolder.isPending

  const handleSubmit = () => {
    if (!name.trim()) return

    if (mode === "create") {
      if (onCreate) {
        onCreate(name.trim())
        onOpenChange(false)
      } else {
        createFolder.mutate(
          { name: name.trim(), parentFolderId: parentFolderId ?? null },
          { onSuccess: () => onOpenChange(false) },
        )
      }
    } else {
      if (!folderId) return
      renameFolder.mutate(
        { id: folderId, data: { name: name.trim() } },
        { onSuccess: () => onOpenChange(false) },
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-card">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Create Folder" : "Rename Folder"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter folder name"
              onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
              autoFocus
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isPending || !name.trim()}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {isPending ? "Saving…" : mode === "create" ? "Create" : "Rename"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
